"use client";

import { useEffect, useMemo, useState } from "react";
import { usePriceInsight } from "@/lib/hooks/usePriceInsight";
import {
  buildPreferenceAttrsFromCapture,
  composeItemRequestTitle,
  formatPreferenceSummary,
  ITEM_REQUEST_NOTES_MAX_LEN,
  evaluateBuyerBudgetGuidance,
} from "@/lib/item-requests";
import { validateSharedFreeTextContactField } from "@/lib/listing/listingFreeTextPolicy";
import {
  emptyFamilySpecs,
  type ListingFamilySpecs,
} from "@/lib/listing/listingFamilyRuntime";
import {
  resolveProfileLocationApplyArgs,
  type SellProfileLocation,
} from "@/lib/listing/listingLocationIds";
import type { HierarchicalLocationOnChange } from "@/lib/locationIds";
import type { CategorySelection, ItemCategory } from "@/lib/types";

export type ItemRequestCaptureSubmitValues = {
  category: ItemCategory;
  subcategory: string;
  categoryName: string;
  subcategoryName: string;
  /** Match area — may differ from profile */
  userLocation: string;
  maxPrice?: number;
  urgency: "low" | "medium" | "high";
  notes?: string;
  budgetSoftWarnAcknowledged?: boolean;
  preferenceAttrs?: Record<string, string | number | boolean>;
};

/** D13 stepwise capture — ≈3 steps (U1). */
export type ItemRequestCaptureStep = "what" | "budget" | "finish";

export const ITEM_REQUEST_CAPTURE_STEPS: readonly ItemRequestCaptureStep[] = [
  "what",
  "budget",
  "finish",
] as const;

export const ITEM_REQUEST_CAPTURE_STEP_META: Record<
  ItemRequestCaptureStep,
  { index: number; title: string; shortLabel: string }
> = {
  what: {
    index: 1,
    title: "What are you looking for?",
    shortLabel: "What you want",
  },
  budget: {
    index: 2,
    title: "What's a realistic budget?",
    shortLabel: "Budget",
  },
  finish: {
    index: 3,
    title: "Where should we look?",
    shortLabel: "Where & when",
  },
};

type Options = {
  profileLocation?: SellProfileLocation | null;
};

/**
 * Owns capture UX state: steps (D13), category, optional prefs, location, budget guidance.
 * Form stays presentational.
 */
export function useItemRequestCapture(options?: Options) {
  const profileLocation = options?.profileLocation ?? null;
  const [step, setStep] = useState<ItemRequestCaptureStep>("what");
  const [selection, setSelectionState] = useState<CategorySelection | null>(
    null,
  );
  const [familySpecs, setFamilySpecs] =
    useState<ListingFamilySpecs>(emptyFamilySpecs());
  const [brand, setBrand] = useState("");
  const [size, setSize] = useState("");
  const [model, setModel] = useState("");
  /** null = follow profile prefill; string = user-chosen match area */
  const [locationDraft, setLocationDraft] = useState<string | null>(null);
  const [maxPrice, setMaxPrice] = useState("");
  const [urgency, setUrgency] = useState<"low" | "medium" | "high">("medium");
  const [notes, setNotes] = useState("");
  const [notesError, setNotesError] = useState<string | null>(null);
  const [budgetWarnAck, setBudgetWarnAck] = useState(false);
  const [needsAckHint, setNeedsAckHint] = useState(false);
  const [stepHint, setStepHint] = useState<string | null>(null);

  const profileLocationValue = useMemo(() => {
    if (!profileLocation?.location?.trim()) return "";
    return resolveProfileLocationApplyArgs(profileLocation).value;
  }, [profileLocation]);

  const location = locationDraft ?? profileLocationValue;

  const resetBudgetAck = () => {
    setBudgetWarnAck(false);
    setNeedsAckHint(false);
  };

  const setSelection = (next: CategorySelection) => {
    const prevId = selection?.subcategoryId;
    setSelectionState(next);
    setStepHint(null);
    if (prevId !== next.subcategoryId) {
      setFamilySpecs(emptyFamilySpecs());
      setBrand("");
      setSize("");
      setModel("");
      resetBudgetAck();
    }
  };

  const handleLocationChange: HierarchicalLocationOnChange = (value) => {
    setLocationDraft(value);
    setStepHint(null);
  };

  const composedTitle = useMemo(() => {
    if (!selection) return "";
    return composeItemRequestTitle({
      categoryName: selection.categoryName,
      subcategoryName: selection.subcategoryName,
    });
  }, [selection]);

  const preferenceAttrs = useMemo(() => {
    if (!selection) return {};
    return buildPreferenceAttrsFromCapture({
      subcategoryId: selection.subcategoryId,
      brand,
      size,
      model,
      familySpecs,
    });
  }, [selection, brand, size, model, familySpecs]);

  const preferenceSummary = useMemo(
    () => formatPreferenceSummary(preferenceAttrs),
    [preferenceAttrs],
  );

  const lookingForLine = useMemo(() => {
    if (!composedTitle) return "";
    return preferenceSummary
      ? `${composedTitle} · ${preferenceSummary}`
      : composedTitle;
  }, [composedTitle, preferenceSummary]);

  const {
    isEnabled: insightEnabled,
    isLoading: insightLoading,
    data: insightData,
    error: insightError,
    getPriceSuggestion,
    clearSuggestion,
  } = usePriceInsight();

  /** Insight only after Step A — so comps use optional prefs (D11/D13). */
  const insightEligible = step === "budget" || step === "finish";

  useEffect(() => {
    if (!insightEnabled || !insightEligible || !selection || !composedTitle) {
      if (!insightEligible) clearSuggestion();
      return;
    }
    const t = setTimeout(() => {
      void getPriceSuggestion(
        composedTitle,
        selection.categoryId,
        selection.subcategoryId,
        undefined,
        {
          listingAttributes:
            Object.keys(preferenceAttrs).length > 0 ? preferenceAttrs : null,
        },
      );
    }, 400);
    return () => clearTimeout(t);
  }, [
    insightEnabled,
    insightEligible,
    selection,
    composedTitle,
    preferenceAttrs,
    getPriceSuggestion,
    clearSuggestion,
  ]);

  const numericMax = maxPrice ? Number(maxPrice.replace(/,/g, "")) : undefined;

  const guidance = useMemo(() => {
    if (!insightData) {
      return evaluateBuyerBudgetGuidance({ maxPrice: numericMax });
    }
    return evaluateBuyerBudgetGuidance({
      maxPrice: numericMax,
      bandMin: insightData.bandMin ?? null,
      rangeMin: insightData.priceRange?.minPrice ?? null,
      suggestedPrice: insightData.suggestedPrice ?? null,
    });
  }, [insightData, numericMax]);

  const handleMaxPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    if (numericValue === "" || !Number.isNaN(Number(numericValue))) {
      setMaxPrice(
        numericValue === "" ? "" : Number(numericValue).toLocaleString("en-NG"),
      );
      resetBudgetAck();
    }
  };

  const setNotesCapped = (value: string) => {
    setNotes(value.slice(0, ITEM_REQUEST_NOTES_MAX_LEN));
    // Clear stale error while editing; re-check on blur / submit (no as-you-type coach).
    if (notesError) setNotesError(null);
  };

  const validateNotesOnBlur = () => {
    setNotesError(
      validateSharedFreeTextContactField(notes, { audience: "request" }) ??
        null,
    );
  };

  const applySuggested = () => {
    const suggested =
      guidance.suggestedMax ??
      insightData?.suggestedPrice ??
      insightData?.bandMin;
    if (typeof suggested === "number" && suggested > 0) {
      setMaxPrice(Math.round(suggested).toLocaleString("en-NG"));
      resetBudgetAck();
    }
  };

  const acknowledgeSoftWarn = () => {
    setBudgetWarnAck(true);
    setNeedsAckHint(false);
  };

  const canLeaveWhat = Boolean(selection?.subcategoryId);
  const canLeaveBudget = (() => {
    if (guidance.showSoftWarn && !budgetWarnAck) return false;
    return true;
  })();
  const canSubmit = Boolean(selection && location.trim());

  const goNext = () => {
    setStepHint(null);
    if (step === "what") {
      if (!canLeaveWhat) {
        setStepHint("Pick a category and subcategory to continue.");
        return;
      }
      setStep("budget");
      return;
    }
    if (step === "budget") {
      if (guidance.showSoftWarn && !budgetWarnAck) {
        setNeedsAckHint(true);
        return;
      }
      setNeedsAckHint(false);
      setStep("finish");
    }
  };

  const goBack = () => {
    setStepHint(null);
    setNeedsAckHint(false);
    if (step === "finish") {
      setStep("budget");
      return;
    }
    if (step === "budget") {
      setStep("what");
    }
  };

  const tryBuildSubmitValues = ():
    | { ok: true; values: ItemRequestCaptureSubmitValues }
    | {
        ok: false;
        reason:
          | "missing_selection"
          | "missing_location"
          | "needs_soft_warn_ack"
          | "notes_policy"
          | "wrong_step";
      } => {
    if (step !== "finish") {
      return { ok: false, reason: "wrong_step" };
    }
    if (!selection) {
      return { ok: false, reason: "missing_selection" };
    }
    const userLocation = location.trim();
    if (!userLocation) {
      setStepHint("Choose where we should look for matches.");
      return { ok: false, reason: "missing_location" };
    }
    if (guidance.showSoftWarn && !budgetWarnAck) {
      setNeedsAckHint(true);
      setStep("budget");
      return { ok: false, reason: "needs_soft_warn_ack" };
    }
    const notesPolicy = validateSharedFreeTextContactField(notes, {
      audience: "request",
    });
    if (notesPolicy) {
      setNotesError(notesPolicy);
      return { ok: false, reason: "notes_policy" };
    }
    setNeedsAckHint(false);
    setStepHint(null);
    setNotesError(null);
    return {
      ok: true,
      values: {
        category: selection.categoryId,
        subcategory: selection.subcategoryId,
        categoryName: selection.categoryName,
        subcategoryName: selection.subcategoryName,
        userLocation,
        maxPrice: numericMax,
        urgency,
        notes: notes.trim() || undefined,
        budgetSoftWarnAcknowledged: budgetWarnAck || undefined,
        preferenceAttrs:
          Object.keys(preferenceAttrs).length > 0 ? preferenceAttrs : undefined,
      },
    };
  };

  const showInsightBlock =
    insightEligible &&
    insightEnabled &&
    Boolean(selection) &&
    !insightError &&
    Boolean(insightData);

  const stepMeta = ITEM_REQUEST_CAPTURE_STEP_META[step];

  return {
    step,
    stepMeta,
    totalSteps: ITEM_REQUEST_CAPTURE_STEPS.length,
    goNext,
    goBack,
    canLeaveWhat,
    canLeaveBudget,
    selection,
    setSelection,
    composedTitle,
    lookingForLine,
    preferenceSummary,
    familySpecs,
    setFamilySpecs,
    brand,
    setBrand,
    size,
    setSize,
    model,
    setModel,
    location,
    handleLocationChange,
    profileLocation,
    maxPrice,
    handleMaxPriceChange,
    urgency,
    setUrgency,
    notes,
    setNotesCapped,
    validateNotesOnBlur,
    notesError,
    notesMaxLen: ITEM_REQUEST_NOTES_MAX_LEN,
    guidance,
    insightLoading,
    insightData,
    showInsightBlock,
    budgetWarnAck,
    needsAckHint,
    stepHint,
    applySuggested,
    acknowledgeSoftWarn,
    tryBuildSubmitValues,
    canSubmit,
  };
}

export type UseItemRequestCapture = ReturnType<typeof useItemRequestCapture>;
